import CarsTableValuationCountryIndicator from '@components/CarsTableValuationCountryIndicator';
import { ColumnMeta } from '@tanstack/react-table';
import { get } from 'lodash';
import { MonetaryAmount, ValuationCountryCode } from 'src/modules/generated/api';
import { Potential } from 'src/types/potential';

import type { BidsTableNextItem, CarsTableNextItem, StrictColumnDef } from '../../types';

import { ContentCell, NumericCell, NumericCellFallback } from '../../cells';
import { ColumnSize } from '../../constants';
import { percentFormat } from '../../format';

export const createPotentialColumn = (
  potential: Potential,
  meta?: ColumnMeta<CarsTableNextItem, unknown>,
  enableSorting: boolean = true,
): StrictColumnDef<CarsTableNextItem> => ({
  id: `potential.${potential}`,
  accessorFn: (car) => get(car, ['potential', potential]),
  cell: (props) => {
    const { original } = props.row;
    // Package bid: use packageAggregation.potential
    const value = (original as BidsTableNextItem).isPackageBid
      ? get(original, ['packageAggregation', 'potential', potential])
      : props.getValue<MonetaryAmount | number | undefined>();
    return (
      <NumericCell
        value={value ?? '-'}
        // format only receives percentage formats as MonetaryAmounts are resolved by NumericCell
        format={percentFormat.format}
      />
    );
  },
  size: ColumnSize.xs,
  meta: {
    isNumeric: true,
    fallback: () => <NumericCellFallback textWidth="80%" />,
    sortKey: potential,
    ...meta,
  },
  enableSorting,
});

export const createCountryPotentialColumn = (
  country: ValuationCountryCode,
  potential: Potential,
  meta?: ColumnMeta<CarsTableNextItem, unknown>,
  enableSorting: boolean = true,
): StrictColumnDef<CarsTableNextItem> => ({
  id: `potential.${country}.${potential}`,
  accessorFn: (car) => get(car, ['potentials', country, potential]),
  cell: (props) => {
    const { original } = props.row;
    // Package bids don't have country-specific potentials, use main potential
    const value = (original as BidsTableNextItem).isPackageBid
      ? get(original, ['packageAggregation', 'potential', potential])
      : props.getValue<MonetaryAmount | number | undefined>();
    return (
      <ContentCell>
        <CarsTableValuationCountryIndicator valuationCountry={country} />
        <NumericCell
          value={value ?? '-'}
          // format only receives percentage formats as MonetaryAmounts are resolved by NumericCell
          format={percentFormat.format}
        />
      </ContentCell>
    );
  },
  size: ColumnSize.xs,
  meta: {
    isNumeric: true,
    fallback: () => <NumericCellFallback textWidth="80%" />,
    ...meta,
  },
  enableSorting,
});
